import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { withRouter } from 'react-router-dom';
import MathJax from 'react-mathjax2';
import uuidv4 from 'uuid/v4';

import { LocalizationPropType } from '../../common/types';
import Markdown from '../../common/Markdown';
import FileDialog from './FileDialog';
import YoutubeDialog from './YoutubeDialog';

const messages = defineMessages({
  fi: {
    id: 'editable-description-fi',
    defaultMessage: ' (suomi)',
  },
  sv: {
    id: 'editable-description-sv',
    defaultMessage: ' (ruotsi)',
  },
  en: {
    id: 'editable-description-en',
    defaultMessage: ' (englanti)',
  },
});

class EditableDescription extends Component {
  static propTypes = {
    competitionId: PropTypes.string,
    description: LocalizationPropType.isRequired,
    text: PropTypes.node.isRequired,
    onChange: PropTypes.func.isRequired,
    markdown: PropTypes.bool,
    fileIsPublic: PropTypes.bool,
  };

  static defaultProps = {
    competitionId: null,
    markdown: false,
    fileIsPublic: false,
  };

  state = {
    id: uuidv4(),
    fileModalLocale: null,
    youtubeModalLocale: null,
  };

  onChange = (event, locale) => {
    const description = {
      ...this.props.description,
      [locale]: event.target.value,
    };

    this.props.onChange(description);
  };

  onFileModalOpen = locale => {
    this.setState({ fileModalLocale: locale });
  };

  onYouTubeModalOpen = locale => {
    this.setState({ youtubeModalLocale: locale });
  };

  onSelectFile = file => {
    const { competitionId, markdown } = this.props;
    const { fileModalLocale: locale } = this.state;

    const desc = this.props.description[locale] || '';
    const path = competitionId
      ? `/api/competitions/${competitionId}/files/${file.id}/content`
      : `/api/files/${file.id}/content`;

    const linkMacro = markdown
      ? `[${file.filename}](${path})`
      : `\\href{${path}}{${file.filename}}`;

    const description = {
      ...this.props.description,
      [locale]: desc + linkMacro,
    };

    this.props.onChange(description);
  };

  onSelectImage = file => {
    const { competitionId, markdown } = this.props;
    const { fileModalLocale: locale } = this.state;

    const desc = this.props.description[locale] || '';
    const path = competitionId
      ? `/api/competitions/${competitionId}/files/${file.id}/content`
      : `/api/files/${file.id}/content`;

    let imgMacro = '';
    if (markdown) {
      imgMacro = `![${file.filename}](${path})`;
    } else {
      const match = /\n+$/.exec(desc);
      const baseLinebreaks = desc.length ? 2 : 0;
      const linebreaks = '\n'.repeat(
        match ? Math.max(0, baseLinebreaks - match[0].length) : baseLinebreaks
      );

      imgMacro = `${linebreaks}$$\\includegraphics{${path}}$$`;
    }

    const description = {
      ...this.props.description,
      [locale]: desc + imgMacro,
    };

    this.props.onChange(description);
  };

  onYoutubeEmbed = shortcode => {
    const { markdown } = this.props;
    const { youtubeModalLocale: locale } = this.state;

    const desc = this.props.description[locale] || '';
    const embedMacro = markdown ? `\n\n:::youtube{${shortcode}}` : '';

    const description = {
      ...this.props.description,
      [locale]: desc + embedMacro,
    };

    this.props.onChange(description);
  };

  render() {
    const {
      competitionId,
      description,
      text,
      markdown,
      fileIsPublic,
    } = this.props;

    return (
      <div className="EditableDescription">
        <FileDialog
          id={`filedialog_${this.state.id}`}
          competitionId={competitionId}
          onSelectFile={this.onSelectFile}
          isPublic={fileIsPublic}
          mimeType="*/*"
        />
        <FileDialog
          id={`imagedialog_${this.state.id}`}
          competitionId={competitionId}
          onSelectFile={this.onSelectImage}
          isPublic={fileIsPublic}
          mimeType="image/*"
        />

        <YoutubeDialog
          id={`youtubedialog_${this.state.id}`}
          onSubmit={this.onYoutubeEmbed}
        />

        {['fi', 'sv', 'en'].map(locale => (
          <div key={locale} className="form-row mb-4">
            <div className="form-group col-6 mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <label className="mb-0" htmlFor={`description-${locale}`}>
                  {text}&nbsp;
                  <FormattedMessage {...messages[locale]} />
                </label>

                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-transparent btn-sm my-0"
                    data-toggle="modal"
                    data-target={`#youtubedialog_${this.state.id}`}
                    onClick={() => this.onYouTubeModalOpen(locale)}
                  >
                    <i className="fab fa-youtube" />
                  </button>

                  <button
                    type="button"
                    className="btn btn-transparent btn-sm my-0"
                    data-toggle="modal"
                    data-target={`#imagedialog_${this.state.id}`}
                    onClick={() => this.onFileModalOpen(locale)}
                  >
                    <i className="fas fa-image" />
                  </button>

                  <button
                    type="button"
                    className="btn btn-transparent btn-sm my-0"
                    data-toggle="modal"
                    data-target={`#filedialog_${this.state.id}`}
                    onClick={() => this.onFileModalOpen(locale)}
                  >
                    <i className="fas fa-file-alt" />
                  </button>
                </div>
              </div>

              <textarea
                className="form-control"
                name={`description-${locale}`}
                id={`description-${locale}`}
                value={description[locale]}
                onChange={e => this.onChange(e, locale)}
              />
            </div>

            <div className="form-group col-6 mb-4">
              <span>
                <FormattedMessage
                  id="editable-description-preview"
                  defaultMessage="Esikatselu"
                />
              </span>

              <div className="container mt-2">
                {markdown ? (
                  <div className="markdown">
                    <Markdown source={description[locale]} />
                  </div>
                ) : (
                  <MathJax.Text text={description[locale]} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }
}

export default withRouter(EditableDescription);
